// packages/web/src/lib/authorityGate.ts — ⚑ A REFUSAL BECOMES A REACH, ONCE.
//
// Ruling A1 (decision `ca4d0c48`), and the voice canon's own §2.6: a blocked
// action converts into a request to whoever can grant it. The server already
// does its whole half — a gated write answers 409 with an OFFER body
// (`{authority:'convert', routeTo, missingCapability, fileAsk:{available,
// actionId, offerToken}}`, deliberately NOT the `{error:{message}}` envelope
// every other failure wears, see packages/server/src/authorityErrors.ts).
//
// ⚠ WHICH MEANS A CLIENT THAT DOES NOT LOOK AT THE BODY SHOWS "request
// failed". ApiError's normalizer finds no `error` key and falls back to that
// literal string (lib/api.ts's `extractError`), so the doctrine's entire
// answer is one `if` away from never happening — and on the Day 3 walk it did
// not happen: a child pressed + › Audition › New Record, hit the gate, read
// "request failed", and `#/asks` stayed at 0 waiting, 0 asked. The product's
// central promise about children was off, and nothing said so.
//
// ⚑ SO THE CONVERSION LIVES HERE, ONCE. It was already written twice
// (WorkOrderDrawer's `handleSaveError`, RecordActions' `doRetire`) and a third
// copy is how the fourth surface gets forgotten. A caller wires
// `handleSaveError` into whatever it already does with a failed write, and
// renders `gate` wherever its refusals go.
import { useCallback, useState } from 'preact/hooks';
import { ApiError, authorityOfferFrom, fileAskFromOffer } from './api';

/** What the gate has to say, as a state rather than a sentence — the words
 *  live below, so two surfaces cannot drift into two vocabularies for one
 *  server answer. */
export type AuthorityGate =
  | { kind: 'filing' }
  | { kind: 'filed' }
  /** The gate refused and there is NO ask on offer — a permanent refusal.
   *  Canon §2.4: a refusal that cannot change gets no retry button, so this
   *  carries a sentence and never an action. */
  | { kind: 'no-road'; message: string }
  /** There WAS a road and the filing missed it. Kept apart from 'no-road' for
   *  the reason RefusalOutcome states below: the form's own Save button is
   *  still there and is worth pressing again, which is not true of a refusal
   *  that cannot change. */
  | { kind: 'ask-failed'; message: string };

/** THE LEAD, identical in all three states on purpose. What happened to the
 *  attempt is the first thing a person needs, and changing the opening line
 *  between "asking…" and "asked" makes a person re-read to find out whether
 *  something went wrong. Nothing went wrong. */
export const AUTHORITY_GATE_LEAD = 'This one is not yours to make.';

/** The rest of the sentence, per state. Receipts, not acknowledgements: each
 *  names what became of the thing and where it now is. */
export function authorityGateWords(gate: AuthorityGate): string {
  if (gate.kind === 'filing') return 'Asking for you…';
  if (gate.kind === 'filed') return 'It became an ask. Whoever can say yes has it, and it is in your Asks.';
  // 'no-road' and 'ask-failed' both carry the server's own sentence, which is
  // what tells the two apart — never a vaguer second copy of ours.
  return gate.message;
}

/** The sentence a permanent refusal carries when the server offered no ask.
 *  Says the two true things — somebody else's to make, and there is no door
 *  here — and offers nothing to press. */
export const AUTHORITY_NO_ROAD = 'It needs someone with more authority, and there is no ask on offer for it here.';

/** What became of a refusal.
 *
 *  ⚠ 'no-road' AND 'ask-failed' ARE NOT THE SAME THING, and collapsing them
 *  is how a retry button ends up on a permanent refusal (canon §2.4, which
 *  forbids exactly that) — or, worse, how a transient blip gets reported as
 *  "this can never be asked". One is the gate's own answer; the other is our
 *  network having a moment, and only the second is worth pressing again. */
export type RefusalOutcome =
  | { outcome: 'filed' }
  | { outcome: 'no-road'; message: string }
  | { outcome: 'ask-failed'; message: string };

/** The conversion itself, without any state — for a caller that already has
 *  its own busy/refusal states and only wants the ask filed (RecordActions'
 *  retire, CaptureSheet's card).
 *
 * Returns null when this was not an authority offer at all; the caller's
 * ordinary error handling still owns those. */
export async function convertRefusalToAsk(tenantId: string, err: unknown): Promise<RefusalOutcome | null> {
  const offer = authorityOfferFrom(err);
  if (!offer) return null;
  const token = offer.fileAsk?.available ? offer.fileAsk.offerToken : undefined;
  if (!token) return { outcome: 'no-road', message: AUTHORITY_NO_ROAD };
  try {
    await fileAskFromOffer(tenantId, token);
    return { outcome: 'filed' };
  } catch (askErr) {
    // In the server's own sentence (canon §2.4: never a vaguer second copy).
    // An ask that could not be filed is a reach that did not happen, and
    // saying "asked" here would be the original silence wearing a better coat.
    return {
      outcome: 'ask-failed',
      message: askErr instanceof ApiError ? askErr.message : 'The ask could not be filed. Try again.',
    };
  }
}

/** The hook a form-shaped surface uses: hand `handleSaveError` to whatever
 *  catches the write, render `gate` where the surface puts its refusals.
 *
 *  `handleSaveError` returns TRUE when it took the error — which is exactly
 *  the contract RecordForm's own optional `onSaveError` prop already
 *  declares, so wiring it is one line and the generic error line stays the
 *  fallback for everything that is not an authority refusal. */
export function useAuthorityGate(tenantId: string): {
  gate: AuthorityGate | null;
  clearGate: () => void;
  handleSaveError: (err: unknown) => boolean;
} {
  const [gate, setGate] = useState<AuthorityGate | null>(null);
  const clearGate = useCallback(() => setGate(null), []);
  const handleSaveError = useCallback(
    (err: unknown): boolean => {
      if (!authorityOfferFrom(err)) return false;
      setGate({ kind: 'filing' });
      void convertRefusalToAsk(tenantId, err).then((result) => {
        if (!result) return; // unreachable: authorityOfferFrom already said yes
        setGate(result.outcome === 'filed' ? { kind: 'filed' } : { kind: result.outcome, message: result.message });
      });
      return true;
    },
    [tenantId]
  );
  return { gate, clearGate, handleSaveError };
}
